/**
 * Block 12 «Калькулятор стоимости мебели» — Zod-контракты.
 *
 * Defines:
 *   - `CalculatorMethodologySchema` — response от `GET /api/calculator/methodology/:type`
 *     (структура полей + sections + applicable). **Формулы НЕ в API** — они
 *     hardcoded в `packages/shared/src/calculator/formulas/*.ts` (PR-B Phase B1).
 *   - `CalculatorProjectPayloadSchema` — содержимое `projects.payload` JSONB column
 *     на сервере (ADR-0019 §Q11.8 IDB ↔ Server mapping). Discriminated union по
 *     `params.type`.
 *   - 8 per-anketa-type params schemas (`KitchenParamsSchema`, `CabinetParamsSchema`,
 *     `BathTabletopParamsSchema`, `BathShelfAboveParamsSchema`, `BathCabinetParamsSchema`,
 *     `TvZoneParamsSchema`, `BalconyParamsSchema`, `OtherParamsSchema`).
 *   - `CalculatorProjectCreateSchema` / `CalculatorProjectResponseSchema` — request/response
 *     для `POST /api/projects` (с client-generated UUID по ADR-0019 §Q11.3).
 *
 * **Формулы** (corpusQ, edgeBand, hdfQ, facadeCount, hinges, countertop) — отложены
 * в PR-B Phase B1, hardcoded pure functions в `packages/shared/src/calculator/formulas/`.
 *
 * References:
 *   - ADR-0019 §Q1 (5 PRs split — этот PR-A Phase A1 = foundation)
 *   - ADR-0019 §Q2 (methodology API + response shape)
 *   - ADR-0019 §Q5 (9 sections А-И AnketaForm)
 *   - ADR-0019 §Q7 (client-side formulas mirror Edge, P-003/P-006)
 *   - ADR-0019 §Q11.8 (IDB ↔ Server projects.payload mapping)
 *   - PRD `docs/prd/v4/10-block-12-calculator.md` (Универсальная структура анкеты)
 *   - data-model.md §12.1 (projects table, payload JSONB)
 *   - Reference pattern: `contracts/configurator.ts` (Block 6 discriminator)
 */

import { z } from 'zod';
import { CALCULATOR_ANKETA_TYPES } from '../domain/calculator-anketa-types-vocabulary';
import { KITCHEN_HANDLE_TYPES, CABINET_HANDLE_TYPES } from '../domain/handle-types-vocabulary';
import { COUNTERTOP_KINDS } from '../domain/countertop-kinds-vocabulary';

// ── Methodology schema (GET /api/calculator/methodology/:type) ────────────

export const CalculatorAnketaTypeSchema = z.enum(CALCULATOR_ANKETA_TYPES);

/** Поле анкеты — описание UI input'а. */
export const CalculatorFieldSchema = z.object({
  name: z.string().min(1).max(50), // 'length', 'height', ...
  label: z.string().min(1).max(100), // 'Длина (мм)'
  kind: z.enum(['number', 'select', 'boolean', 'text']),
  options: z.array(z.string().min(1).max(100)).optional(), // для kind='select'
  required: z.boolean().default(false),
  min: z.number().optional(),
  max: z.number().optional(),
  helpText: z.string().max(500).optional(),
});

/**
 * Section ID — какие секции анкеты применимы.
 * А «Параметры» и И «Итог» — meta (всегда), не в этом enum.
 * З «+ Добавить позицию» — всегда доступна, не в enum.
 */
export const CalculatorSectionIdSchema = z.enum([
  'parameters', // А: Параметры изделия (всегда)
  'materials', // Б: Материалы (всегда)
  'additional', // В: Дополнительные элементы
  'filling', // Г: Наполнение (только Шкаф/Балкон)
  'lighting', // Д: Подсветка
  'consumables', // Е: Расходники монтажа
  'services', // Ж: Услуги
]);

/** Конкретная additional опция — для секции В. */
export const CalculatorAdditionalSchema = z.enum([
  'facade-radius', // Фасад радиусный
  'aluminum-glass-vitrine', // Витрина в алюминиевом профиле
  'metallic-legs', // Металлические опоры
  'decorative-feet', // Ножки (только ТВ-зона)
  'dishwasher-top', // Посудосушилка верхнего шкафа (только Кухня)
  'dishwasher-bottom', // Посудосушилка нижнего шкафа (только Кухня)
  'laundry-basket', // Корзина для белья (Тумба + Шкаф санузла)
  // ── Hotfix Step 2 (ADR-0021) ───────────────────────────────────────────
  'basket-pullout', // Корзина выдвижная для углового модуля (только Кухня)
  'foldable-facade', // Складной фасад (только Кухня)
  'trash-bin', // Мусорное ведро встроенное (Кухня + Шкаф санузла)
]);

export const CalculatorMethodologySchema = z.object({
  type: CalculatorAnketaTypeSchema,
  fields: z.array(CalculatorFieldSchema).min(1).max(20),
  defaultSections: z.array(CalculatorSectionIdSchema).min(2),
  applicableAdditional: z.array(CalculatorAdditionalSchema).default([]),
  showCountertop: z.boolean().default(false),
  showFilling: z.boolean().default(false),
});

export type CalculatorMethodology = z.infer<typeof CalculatorMethodologySchema>;

// ── Per-type params schemas (discriminated union по `type`) ───────────────

/**
 * Параметры изделия — общие dimensions (length обязателен кроме 'other').
 * 100mm — 10m диапазон для length/length2, 100mm — 5m для height, 100mm — 2m для depth.
 */
const BaseDimensionsSchema = z.object({
  length: z.number().int().min(100).max(10000),
  height: z.number().int().min(100).max(5000).optional(),
  depth: z.number().int().min(100).max(2000).optional(),
  length2: z.number().int().min(100).max(10000).optional(), // для угловых
});

export const KitchenParamsSchema = BaseDimensionsSchema.extend({
  type: z.literal('kitchen'),
  isAngular: z.boolean().default(false),
  hasPenal: z.boolean().default(false),
  fridgeType: z.enum(['open', 'built-in']).default('open'),
  hasShelfAboveFridge: z.boolean().default(false),
  /**
   * Phase B3 Step 3 — тип открывания фасадов (3 опции, включая «гола»).
   * Required per методике §2.1 В.1. Per ТЗ §A.4 spec — user picks один из
   * KITCHEN_HANDLE_TYPES при создании анкеты.
   */
  handleType: z.enum(KITCHEN_HANDLE_TYPES),
  /**
   * Phase B3 Step 3 — тип столешницы (5 опций). Required per методике §2.1 Б.6.
   * Aibek канон 14.05.2026 +: user picks один из COUNTERTOP_KINDS.
   */
  countertopKind: z.enum(COUNTERTOP_KINDS),
  /**
   * Phase B3 Step 3 — длина листа столешницы ЛДСП (3 или 4 м, user picks).
   * Required только если `countertopKind === 'Столешницы ЛДСП'` (refinement
   * ниже). Для HPL/Камень — undefined (HPL фикс 4000 hardcoded в B2 formula
   * per ADR-0033, Камень — pm независимо от листа). Per OQ-3 ответ Aibek.
   */
  countertopSheetLen: z.union([z.literal(3000), z.literal(4000)]).optional(),
});

/**
 * Phase B3 Step 3 — domain-level refinements для Кухни (per методике §2.1 + ТЗ §A.4).
 *
 * Вынесено в named function, потому что Zod v3 `z.discriminatedUnion()` принимает
 * только pure `ZodObject` (без `.refine`/`.superRefine`/`.transform`). Применяется
 * в двух местах:
 *   1. `KitchenParamsRefinedSchema = KitchenParamsSchema.superRefine(...)` —
 *      standalone parse в UI form layer (`useAnketaForm` validates kitchen state).
 *   2. `CalculatorParamsSchema = discriminatedUnion([base...]).superRefine(...)` —
 *      server-side request body parse + при сохранении в IDB через
 *      `CalculatorProjectPayloadSchema.params`.
 *
 * Оба пути дают идентичную семантику валидации (паттерн ADR-0018 §Q1
 * `customTagRefinement` для FeedbackCreateSchema).
 *
 * Refinements (cross-field invariants beyond simple z.enum/z.number bounds):
 *   - **H ≥ 600** (per OQ-D / методике §2.1 §A note: высота кухни не бывает < 600мм,
 *     это минимальная высота цоколя; иначе формула `(H-600) × L` даёт ≤0 площадь).
 *   - **isAngular ⟹ length2 defined && L + L2 > 600** (per методике §2.1 §A note +
 *     `corpusQ`/`facadeCount` формулы требуют positive totalL).
 *   - **countertopKind === 'Столешницы ЛДСП' ⟹ countertopSheetLen defined**
 *     (per OQ-3: пользователь обязан выбрать 3000 или 4000 мм для ЛДСП).
 *
 * handleType/countertopKind присутствие enforced на schema-level (z.enum,
 * Option B per Aibek 14.05.2026 — type-safety at compile time > runtime
 * refinement). Refinement охватывает только cross-field invariants.
 */
function kitchenParamsRefinement(
  data: z.infer<typeof KitchenParamsSchema>,
  ctx: z.RefinementCtx,
): void {
  // height required per методике §2.1 §A line 152 («Required: да»). BaseDimensionsSchema
  // оставляет .optional() для совместимости с draft form filling, но при submission
  // через KitchenParamsRefinedSchema (UI form layer) — обязателен. Code-reviewer
  // 14.05.2026 medium severity fix.
  if (data.height === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['height'],
      message: 'Высота кухни обязательна (per методике §2.1 §A)',
    });
  } else if (data.height < 600) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['height'],
      message: 'Высота кухни не может быть меньше 600 мм (минимальная высота цоколя, per OQ-D)',
    });
  }
  if (data.isAngular) {
    if (data.length2 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['length2'],
        message: 'Угловая кухня: укажите вторую длину (length2)',
      });
    } else if (data.length + data.length2 <= 600) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['length2'],
        message: 'Угловая кухня: length + length2 должно быть > 600 мм',
      });
    }
  }
  if (data.countertopKind === 'Столешницы ЛДСП' && data.countertopSheetLen === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['countertopSheetLen'],
      message: 'Для столешницы ЛДСП необходимо выбрать длину листа (3000 или 4000 мм, per OQ-3)',
    });
  }
}

/**
 * Refined schema для Кухня — используется UI form layer (`useAnketaForm`)
 * для validation kitchen-specific state. Server boundary использует
 * `CalculatorParamsSchema` (discriminated union с union-level `.superRefine`).
 */
export const KitchenParamsRefinedSchema = KitchenParamsSchema.superRefine(kitchenParamsRefinement);

export const CabinetParamsSchema = BaseDimensionsSchema.extend({
  type: z.literal('cabinet'),
  isAngular: z.boolean().default(false),
  isBuiltIn: z.boolean().default(false),
  /**
   * Phase B3 Step 4 — тип открывания фасадов (2 опции: «с ручками» / «от
   * нажатия»). Гола НЕ применяется к Шкафу (per методике §2.2 В.1). Required
   * per ТЗ §A.5 spec.
   */
  handleType: z.enum(CABINET_HANDLE_TYPES),
});

/**
 * Phase B3 Step 4 — cross-field refinement для Шкафа (per методике §2.2 §A).
 *
 * **isAngular ⟹ length2 defined && L + L2 > 600**: угловой шкаф требует
 * positive totalL (формула `corpusQCabinet`, `facadeCountCabinet`).
 *
 * handleType присутствие enforced на schema-level (z.enum).
 */
function cabinetParamsRefinement(
  data: z.infer<typeof CabinetParamsSchema>,
  ctx: z.RefinementCtx,
): void {
  if (data.isAngular) {
    if (data.length2 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['length2'],
        message: 'Угловой шкаф: укажите вторую длину (length2)',
      });
    } else if (data.length + data.length2 <= 600) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['length2'],
        message: 'Угловой шкаф: length + length2 должно быть > 600 мм',
      });
    }
  }
}

/** Refined schema для Шкафа (UI form layer). */
export const CabinetParamsRefinedSchema = CabinetParamsSchema.superRefine(cabinetParamsRefinement);

export const BathTabletopParamsSchema = BaseDimensionsSchema.extend({
  type: z.literal('bath-tabletop'),
  hasWashingMachine: z.boolean().default(false),
  /**
   * Phase B3 Step 5 — тип столешницы (required per ТЗ §A.6). Тумба-ванна
   * имеет столешницу всегда. PR-A1 (ADR-0033): ЛДСП — user выбирает 3/4 м
   * (как Кухня), HPL — фикс 4 м, Камень — pm.
   */
  countertopKind: z.enum(COUNTERTOP_KINDS),
  /**
   * Hotfix-2C Phase C2 Step 5 — Schemas v2 (2026-05-17, line 144) добавляет
   * «Длина листа столешницы ЛДСП (мм)» в Параметры Сан Узла: тумбы.
   * Required только когда `countertopKind === 'Столешницы ЛДСП'` (per
   * refinement ниже). Same union shape что Kitchen countertopSheetLen.
   */
  countertopSheetLen: z.union([z.literal(3000), z.literal(4000)]).optional(),
  /**
   * Hotfix-2C Phase C2 Step 5 — Schemas v2 (line 142): «Тип открывания фасадов
   * (ручки / от нажатия) — 2 опции». `.default('с ручками')` для backward compat
   * с existing fixtures (Phase A1-B3); Zod parse заполняет default если field
   * отсутствует в input. UI рендерит RadioCard с required selection.
   */
  handleType: z.enum(CABINET_HANDLE_TYPES).default('с ручками'),
});

/**
 * Hotfix-2C Phase C2 Step 5 — refinement BathTabletop: `countertopKind ===
 * 'Столешницы ЛДСП' ⟹ countertopSheetLen defined` (mirror Kitchen).
 */
function bathTabletopParamsRefinement(
  data: z.infer<typeof BathTabletopParamsSchema>,
  ctx: z.RefinementCtx,
): void {
  if (data.countertopKind === 'Столешницы ЛДСП' && data.countertopSheetLen === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['countertopSheetLen'],
      message: 'Тумба ЛДСП: укажите длину листа (countertopSheetLen)',
    });
  }
}

export const BathTabletopParamsRefinedSchema = BathTabletopParamsSchema.superRefine(
  bathTabletopParamsRefinement,
);

export const BathShelfAboveParamsSchema = BaseDimensionsSchema.extend({
  type: z.literal('bath-shelf-above'),
  /**
   * Hotfix-2C Phase C2 Step 5 — Schemas v2 (line 192) добавляет
   * «Тип открывания фасадов (ручки / от нажатия)» в Параметры. `.default()` —
   * backward compat для existing fixtures.
   */
  handleType: z.enum(CABINET_HANDLE_TYPES).default('с ручками'),
});

export const BathCabinetParamsSchema = BaseDimensionsSchema.extend({
  type: z.literal('bath-cabinet'),
  hasWashingMachine: z.boolean().default(false),
  hasDryerMachine: z.boolean().default(false),
  /**
   * Hotfix-2C Phase C2 Step 5 — Schemas v2 (line 235) добавляет
   * «Тип открывания фасадов (ручки / от нажатия)» в Параметры. `.default()` —
   * backward compat для existing fixtures.
   */
  handleType: z.enum(CABINET_HANDLE_TYPES).default('с ручками'),
});

/**
 * Phase B3 Step 5 — refinement для Шкафа санузла (per OQ-9 ответ Aibek).
 *
 * **hasDryerMachine ⟹ hasWashingMachine**: сушильная машинка требует наличия
 * стиральной (хранение сушильной над стиралкой — стандартный паттерн санузла).
 * Per методике §2.5 §A note + xlsx variants:
 *   - washing=false, drying=false → формула фасадов variant 1
 *   - washing=true, drying=false → variant 2 (−0.6 м²)
 *   - washing=true, drying=true → variant 3 (−1.2 м²)
 *   - washing=false, drying=true → **невалидно** (этот refinement)
 */
function bathCabinetParamsRefinement(
  data: z.infer<typeof BathCabinetParamsSchema>,
  ctx: z.RefinementCtx,
): void {
  if (data.hasDryerMachine && !data.hasWashingMachine) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hasDryerMachine'],
      message: 'Сушильная машинка требует наличия стиральной (OQ-9: drying ⟹ washing)',
    });
  }
}

/** Refined schema для Шкафа санузла (UI form layer). */
export const BathCabinetParamsRefinedSchema = BathCabinetParamsSchema.superRefine(
  bathCabinetParamsRefinement,
);

export const TvZoneParamsSchema = BaseDimensionsSchema.extend({
  type: z.literal('tv-zone'),
  hasShelf: z.boolean().default(false),
  shelfLength: z.number().int().min(100).max(10000).optional(),
  shelfHeight: z.number().int().min(100).max(5000).optional(),
  shelfDepth: z.number().int().min(100).max(2000).optional(),
  /**
   * Hotfix-2C Phase C2 Step 5 — Schemas v2 (line 290) добавляет
   * «Тип открывания фасадов (ручки / от нажатия)» в Параметры (subgroup
   * «ОБЩИЕ» per hotfix-2B Phase B5 Step 11 sub-grouping). `.default()` для
   * backward compat. ТВ-зона: 2 опции (без «гола»).
   */
  handleType: z.enum(CABINET_HANDLE_TYPES).default('с ручками'),
  /**
   * Phase B3 Step 6 — витрина из профиля (ТВ-зона анкета B10 per методике §2.6).
   * `.default(false)` — backward compat с Phase A1 minimal tv-zone literals
   * (e.g., `{ type: 'tv-zone', length: 2000, hasShelf: false }`). B2 fixtures
   * explicitly pass `vitrineProfile: false` для type-compat.
   *
   * `vitrineProfile=true` влияет на формулу `areaFacade` ТВ-зоны (если есть
   * shelfBlock, то стенки шкафа без фасадов — только 2D глубины) per §2.6 Б.1.
   * Передаётся в B2 `areaFacadeTvZone` через `TvZoneAreaFacadeOptions` (отдельный
   * arg, не из params).
   */
  vitrineProfile: z.boolean().default(false),
});

export const BalconyParamsSchema = BaseDimensionsSchema.extend({
  type: z.literal('balcony'),
  /**
   * Phase B3 Step 4 (per OQ-13 ответ Aibek) — балкон может быть угловым.
   * `.default(false)` — backward compat с Phase A1 minimal балкон literals
   * (e.g., `{ type: 'balcony', length: 1000 }`). B2 fixtures explicitly pass
   * `isAngular: false` для type-compat (как Cabinet pattern).
   */
  isAngular: z.boolean().default(false),
  /**
   * Phase B3 Step 4 — тип открывания (2 опции, без «гола», per методике §2.7).
   * Required per ТЗ §A.5 spec.
   */
  handleType: z.enum(CABINET_HANDLE_TYPES),
});

/**
 * Phase B3 Step 4 — cross-field refinement для Балкона (per методике §2.7 + OQ-13).
 *
 * **isAngular ⟹ length2 defined && L + L2 > 600**: угловой балкон требует
 * positive totalL (формула `corpusQBalcony`, `facadeCountBalcony` — Вариант A
 * per вопросу B методики).
 */
function balconyParamsRefinement(
  data: z.infer<typeof BalconyParamsSchema>,
  ctx: z.RefinementCtx,
): void {
  if (data.isAngular) {
    if (data.length2 === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['length2'],
        message: 'Угловой балкон: укажите вторую длину (length2)',
      });
    } else if (data.length + data.length2 <= 600) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['length2'],
        message: 'Угловой балкон: length + length2 должно быть > 600 мм',
      });
    }
  }
}

/** Refined schema для Балкона (UI form layer). */
export const BalconyParamsRefinedSchema = BalconyParamsSchema.superRefine(balconyParamsRefinement);

/**
 * «Другая мебель» (2.12.9) — свободный режим без dimensions/формул, только название.
 * `.strict()` отвергает extra keys (передан `length` → ошибка). Нужно чтобы клиент
 * не путал свободный режим с типизированной анкетой.
 */
export const OtherParamsSchema = z
  .object({
    type: z.literal('other'),
  })
  .strict();

/**
 * Discriminated union по `type` (pure ZodDiscriminatedUnion).
 *
 * Базовые типы (z.enum, z.number, z.boolean) enforced каждой member schema.
 * Cross-field refinements (H ≥ 600, isAngular ⟹ length2, ЛДСП ⟹ sheetLen)
 * НЕ применяются на уровне union — Aibek 14.05.2026 (Option B): CalculatorParamsSchema
 * остаётся ZodDiscriminatedUnion для clean consumer interface (не ZodEffects).
 *
 * Refinements живут на per-anketa refined schemas (например,
 * `KitchenParamsRefinedSchema`), используемых UI form layer (`useAnketaForm`).
 * Server boundary при необходимости делает re-parse через type-specific
 * refined schema после discriminator routing.
 */
export const CalculatorParamsSchema = z.discriminatedUnion('type', [
  KitchenParamsSchema,
  CabinetParamsSchema,
  BathTabletopParamsSchema,
  BathShelfAboveParamsSchema,
  BathCabinetParamsSchema,
  TvZoneParamsSchema,
  BalconyParamsSchema,
  OtherParamsSchema,
]);

export type CalculatorParams = z.infer<typeof CalculatorParamsSchema>;

// ── Project payload (server `projects.payload` JSONB) ─────────────────────

/**
 * Свободная позиция в смете (секция «+ Добавить позицию», PRD §10 секция З).
 * Цена в тенге, hard cap 1e9 для symmetry с `QuoteCreateSchema.cost` (sanity).
 * `quantity` допускает дробные значения (1.5 м кабеля, 0.5 кг крепежа) — `unit`
 * объясняет семантику.
 */
export const CalculatorFreeItemSchema = z.object({
  name: z.string().min(1).max(200),
  quantity: z.number().nonnegative(),
  unit: z.string().min(1).max(20),
  price: z.number().nonnegative().max(1_000_000_000),
});

/**
 * Calculator project payload — содержимое `projects.payload` JSONB column.
 *
 * `furnitureType` хранится как **raw string** для backward-compatibility с
 * future админ-расширением типов (если в Phase 9+ добавится новый furniture
 * type — старые сохранённые проекты не сломаются).
 *
 * **PR-H7 (ADR-0030):** `Quote` (КП) теперь тоже использует `z.string().min(1).max(100)`
 * (раньше — `z.enum(QUOTE_FURNITURE_TYPES)`) для синхронизации labels Calculator
 * entry → Quote → PDF + поддержки «Свой вариант» free-text input. Оба слоя теперь
 * raw strings; разделение через explicit max lengths (calculator 50 / quote 100):
 *   - `projects.payload.furnitureType` (max 50) — anketa-level identifier
 *   - `quotes.furnitureType` (max 100) — display label включая custom strings
 * См. ADR-0030 §1 + jsdoc в `contracts/quote.ts`.
 *
 * Material/cost поля будут детализированы в PR-B Phase B1 при integration с
 * pure-function formulas (`calculator/formulas/*`). Phase A1 фиксирует минимально
 * необходимый shape — итоговая `total`, manual `freeItems`, optional material prices.
 */
export const CalculatorProjectPayloadSchema = z.object({
  itemName: z.string().min(1).max(200),
  params: CalculatorParamsSchema,
  furnitureType: z.string().min(1).max(50),
  // Material costs (Section Б) — все optional, заполняются по мере прохождения анкеты.
  // `.max(1_000_000_000)` — sanity cap для symmetry с `QuoteCreateSchema.cost` (Issue
  // из code-review Phase A1: без cap клиент мог бы прислать `total: 1e20`).
  facadeType: z.string().min(1).max(50).optional(),
  facadePrice: z.number().nonnegative().max(1_000_000_000).optional(),
  /**
   * PR-H1 Round 2 (ADR-0026) — replace `corpusBrand: string` на generic map
   * `brandSelections: Record<rowId, brand>` для per-row brand storage всех
   * material/furniture rows (фасады, корпус, петли, ручки, ...). Backward-compat
   * для старых IDB записей где `corpusBrand` лежал отдельно — через migration
   * code в `AnketaFormContainer.tsx` (load path) + `seedFromAnketa.ts` (seed
   * path), ~5 строк с `@ts-expect-error` подавлениями. Cleanup tracked в B-167.
   */
  brandSelections: z.record(z.string().max(100)).optional(),
  corpusPrice: z.number().nonnegative().max(1_000_000_000).optional(),
  edgeBandPrice: z.number().nonnegative().max(1_000_000_000).optional(),
  hdfPrice: z.number().nonnegative().max(1_000_000_000).optional(),
  freeItems: z.array(CalculatorFreeItemSchema).default([]),
  total: z.number().nonnegative().max(1_000_000_000),
});

export type CalculatorProjectPayload = z.infer<typeof CalculatorProjectPayloadSchema>;

// ── Project mutation schemas (POST /api/projects, PUT /api/projects/:id) ──

/**
 * `id` client-generated (ADR-0019 §Q11.3 local UUID), сервер использует как
 * idempotent primary key. `kind: 'calculator-anketa'` — discriminator на
 * server-side для `projects` table (vs 'free' для «Другая мебель», добавится
 * в PR-C при integration with 2.12.9).
 */
export const CalculatorProjectCreateSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(200),
  kind: z.literal('calculator-anketa'),
  payload: CalculatorProjectPayloadSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const CalculatorProjectResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  kind: z.literal('calculator-anketa'),
  payload: CalculatorProjectPayloadSchema,
  status: z.enum(['draft', 'completed']),
  userId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type CalculatorProjectCreate = z.infer<typeof CalculatorProjectCreateSchema>;
export type CalculatorProjectResponse = z.infer<typeof CalculatorProjectResponseSchema>;
